import { useCookie, useNuxtApp } from "#app";
import { get, isArray, isObject, isString, set } from "lodash-es";

import { LOCALE_COOKIE_KEY } from "./localePreference";

function findDeepValText(currentItem = {}, findKeys = [], transferList = []) {
  findKeys.map((keys) => {
    keys.map((key, keyIx) => {
      const selectItem = get(currentItem, key);
      if (isArray(selectItem)) {
        selectItem.map((o) => {
          findDeepValText(o, [keys.slice(keyIx + 1)], transferList);
        });
      } else if (isObject(selectItem)) {
        findDeepValText(selectItem, [keys.slice(keyIx + 1)], transferList);
      } else if (isString(selectItem)) {
        transferList.push(selectItem);
      }
    });
  });
}

export function getHeaderValue(headers = {}, key = "") {
  if (!headers || !key) {
    return "";
  }
  if (typeof headers.get === "function") {
    return headers.get(key) || headers.get(key.toLowerCase()) || "";
  }
  return headers[key] || headers[key.toLowerCase()] || "";
}

export function getTranslatedText(sourceText, transfersMap = {}) {
  const normalizedText = String(sourceText ?? "").trim();
  if (!normalizedText) {
    return "";
  }
  return transfersMap?.[normalizedText] || normalizedText;
}

export function getCurrentLocaleTransferCode(locale) {
  const localeCode = String(locale || "zh-CN").trim();
  const codeMap = {
    en: "en",
    "en-US": "en",
    vi: "vi",
    vi_VN: "vi",
    "vi-VN": "vi",
    ja: "ja",
    jp: "ja",
    ja_JP: "ja",
    "ja-JP": "ja",
    "zh-TW": "zh-tw",
    "zh-Hant": "zh-tw",
    tw: "zh-tw",
  };

  if (codeMap[localeCode]) {
    return codeMap[localeCode];
  }

  if (localeCode.toLowerCase().startsWith("zh")) {
    return "";
  }

  return localeCode;
}

export function resolveCurrentTransferLanguage(defaultLanguage = "") {
  let locale = String(defaultLanguage || "").trim();

  try {
    const { $i18n } = useNuxtApp();
    if ($i18n?.locale) {
      locale = typeof $i18n.locale === "function" ? $i18n.locale() : $i18n.locale.value;
    }
  } catch (error) {}

  if (!locale) {
    try {
      const localeCookie = useCookie(LOCALE_COOKIE_KEY, { path: "/" });
      locale = String(localeCookie.value || "").trim();
    } catch (error) {}
  }

  return String(locale || defaultLanguage || "").trim();
}

function normalizeTransferKeysConfig(rawConfig) {
  if (isArray(rawConfig)) {
    return rawConfig;
  }
  if (!rawConfig) {
    return [];
  }
  try {
    const parsedConfig = JSON.parse(rawConfig);
    return isArray(parsedConfig) ? parsedConfig : [];
  } catch (error) {
    return [];
  }
}

function parseRequestBody(body) {
  if (!body) {
    return {};
  }
  if (typeof body === "string") {
    try {
      return JSON.parse(body);
    } catch (error) {
      return {};
    }
  }
  return isObject(body) ? body : {};
}

function parseTranslateTexts(translateResponse) {
  const transferRes = get(translateResponse, "data.result");
  const transferResults =
    get(translateResponse, "data.results") || get(translateResponse, "data.data.results") || [];
  const transferData = get(translateResponse, "data.data");
  const transferListData = get(translateResponse, "data.list");
  const transferResultData = get(translateResponse, "data.resultList");

  if (isArray(transferResults) && transferResults.length) {
    return transferResults
      .slice()
      .sort((a, b) => Number(a?.index || 0) - Number(b?.index || 0))
      .map((item) => item?.translatedText || "");
  }

  if (isArray(transferData)) {
    return transferData;
  }

  if (isArray(transferListData)) {
    return transferListData;
  }

  if (isArray(transferResultData)) {
    return transferResultData;
  }

  if (typeof transferRes === "string") {
    return transferRes.split("\n").map((item) => String(item || ""));
  }

  return [];
}

export function resolveTransferRequestConfig(headers = {}, body) {
  const requestBody = parseRequestBody(body);
  const transferConfig = get(requestBody, "___transferConfig", {});
  const bodyTransferKeysConfig = normalizeTransferKeysConfig(
    get(transferConfig, "transferKeysConfig")
  );
  const headerTransferKeysConfig = normalizeTransferKeysConfig(
    getHeaderValue(headers, "transferKeysConfig")
  );

  return {
    language: String(
      get(transferConfig, "language") || getHeaderValue(headers, "language") || ""
    ),
    transferKeysConfig:
      bodyTransferKeysConfig.length > 0
        ? bodyTransferKeysConfig
        : headerTransferKeysConfig,
    formatType: String(get(transferConfig, "transferType") || "text"),
    setPath: get(transferConfig, "setPath"),
  };
}

export async function applyTransferTranslations({
  responseData,
  transferConfig,
  mallAuthorization,
  translate,
}) {
  if (
    !process.client ||
    !mallAuthorization ||
    !responseData ||
    !transferConfig?.language ||
    !transferConfig?.transferKeysConfig?.length ||
    typeof translate !== "function"
  ) {
    return;
  }

  const targetLanguage = getCurrentLocaleTransferCode(transferConfig.language);
  if (!targetLanguage) {
    return;
  }

  let transferList = [];
  findDeepValText(responseData, transferConfig.transferKeysConfig, transferList);
  transferList = Array.from(
    new Set(
      transferList
        .map((item) => String(item ?? "").trim())
        .filter(Boolean)
    )
  );

  if (!transferList.length) {
    return;
  }

  const transfersMap = {};
  for (let i = 0; i < transferList.length; i += 100) {
    const chunk = transferList.slice(i, i + 100);
    try {
      const translateResponse = await translate({
        isSystemText: 0,
        texts: chunk,
        sourceLanguage: "zh",
        targetLanguage,
        appType: "react-mall-app",
        formatType: transferConfig.formatType || "text",
      });
      const translateSuccess = get(translateResponse, "success");
      if (translateSuccess === false) {
        chunk.forEach((item) => {
          transfersMap[item] = item;
        });
        continue;
      }

      const translatedTexts = parseTranslateTexts(translateResponse);
      chunk.forEach((item, index) => {
        transfersMap[item] = translatedTexts[index] || item;
      });
    } catch (error) {
      chunk.forEach((item) => {
        transfersMap[item] = item;
      });
    }
  }

  if (!Object.keys(transfersMap).length) {
    return;
  }

  if (transferConfig.setPath === "null") {
    responseData.transfersMap = transfersMap;
    return;
  }

  set(responseData, transferConfig.setPath || "data.transfersMap", transfersMap);
}
